import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Users, Copy, Crown } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const RELATIONS = ["Titular", "Pareja", "Hijo/a", "Otro familiar"];
const ROLE_LABELS = { admin: "Administrador", editor: "Editor", consulta: "Consulta" };
const EMPTY = { name: "", birth_date: "", relation: "Pareja" };

export default function Familia() {
  const { user, refreshUser } = useAuth();
  const [fam, setFam] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [joinCode, setJoinCode] = useState("");

  const isAdmin = fam?.my_role === "admin";
  const load = () => api.get("/family").then((r) => setFam(r.data));
  useEffect(() => { load(); }, []);

  const saveFamily = async (patch) => {
    try {
      await api.put("/family", patch);
      toast.success("Unidad familiar actualizada");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitMember = async () => {
    try {
      const payload = { name: form.name, birth_date: form.birth_date || null, relation: form.relation };
      if (editId) await api.put(`/family/members/${editId}`, payload);
      else await api.post("/family/members", payload);
      toast.success(editId ? "Miembro actualizado" : "Miembro añadido");
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeMember = async (id) => {
    try { await api.delete(`/family/members/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const setRole = async (userId, role) => {
    try { await api.put("/family/role", { user_id: userId, role }); toast.success("Rol actualizado"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const join = async () => {
    try {
      const { data } = await api.post("/family/join", { code: joinCode });
      toast.success(`Te has unido a ${data.family_name}`);
      setJoinCode("");
      await refreshUser();
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!fam) return <div className="text-sm text-muted-foreground">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="familia-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Unidad familiar</h1>
        <p className="text-sm text-muted-foreground mt-1">Gestión conjunta del patrimonio familiar.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Datos y plan */}
        <div className="border border-border rounded-lg p-5 space-y-4">
          <div className="flex items-center gap-2"><Users size={18} /><h3 className="font-heading font-bold text-lg">{fam.name}</h3></div>
          {isAdmin && (
            <div className="flex gap-2">
              <Input data-testid="family-name-input" defaultValue={fam.name} onBlur={(e) => e.target.value !== fam.name && saveFamily({ name: e.target.value })} />
            </div>
          )}
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold mb-2">Plan</div>
            <div className="flex gap-2">
              {[["individual", "Individual", "1 usuario · 1 unidad financiera"], ["familiar", "Familiar", "Hasta 5 usuarios · patrimonio compartido"]].map(([k, l, d]) => (
                <button
                  key={k}
                  data-testid={`plan-${k}`}
                  disabled={!isAdmin}
                  onClick={() => fam.plan !== k && saveFamily({ plan: k })}
                  className={`flex-1 text-left border rounded-lg p-3 transition-colors ${fam.plan === k ? "border-foreground bg-primary text-primary-foreground" : "border-border hover:bg-secondary"} ${!isAdmin ? "opacity-60" : ""}`}
                >
                  <div className="text-sm font-bold">{l}</div>
                  <div className={`text-xs ${fam.plan === k ? "text-white/60" : "text-muted-foreground"}`}>{d}</div>
                </button>
              ))}
            </div>
          </div>
          {isAdmin && fam.invite_code && (
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold mb-1">Código de invitación</div>
              <div className="flex items-center gap-2">
                <code data-testid="invite-code" className="bg-secondary px-3 py-1.5 rounded-lg font-semibold tracking-widest">{fam.invite_code}</code>
                <button data-testid="copy-invite-code" onClick={() => { navigator.clipboard.writeText(fam.invite_code); toast.success("Código copiado"); }} className="h-8 w-8 flex items-center justify-center rounded-lg border border-border hover:bg-secondary"><Copy size={14} /></button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{fam.plan === "familiar" ? "Compártelo para que otros usuarios se unan (máx. 5)." : "Cambia a plan Familiar para poder invitar."}</p>
            </div>
          )}
        </div>

        {/* Usuarios y roles */}
        <div className="border border-border rounded-lg p-5">
          <h3 className="font-heading font-bold text-lg mb-3">Usuarios ({fam.users.length}{fam.plan === "familiar" ? "/5" : "/1"})</h3>
          <div className="divide-y divide-border" data-testid="family-users-list">
            {fam.users.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-2.5 gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-1.5">{u.name || u.email}{u.family_role === "admin" && <Crown size={12} className="text-amber-500" />}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
                {isAdmin && u.id !== user.id ? (
                  <Select value={u.family_role} onValueChange={(v) => setRole(u.id, v)}>
                    <SelectTrigger className="w-36 h-8 text-xs" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(ROLE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <span className="text-xs font-semibold bg-secondary px-2 py-1 rounded">{ROLE_LABELS[u.family_role] || u.family_role}</span>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 border-t border-border pt-3">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold mb-1">Unirme a otra unidad familiar</div>
            <div className="flex gap-2">
              <Input data-testid="join-code-input" placeholder="Código de invitación" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} />
              <Button data-testid="join-submit-button" variant="outline" onClick={join} disabled={!joinCode}>Unirme</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Miembros */}
      <div className="border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-lg">Miembros de la familia ({fam.members.length})</h3>
          {isAdmin && (
            <Button data-testid="add-member-button" size="sm" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }}>
              <Plus size={14} className="mr-1" /> Añadir miembro
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-3">Asocia opcionalmente ingresos y gastos a cada miembro para saber quién los genera.</p>
        <div className="divide-y divide-border" data-testid="members-list">
          {fam.members.map((m) => (
            <div key={m.id} className="flex items-center justify-between py-2.5">
              <div>
                <div className="text-sm font-medium">{m.name}</div>
                <div className="text-xs text-muted-foreground">{m.relation}{m.birth_date ? ` · ${m.birth_date.split("-").reverse().join("/")}` : ""}{m.user_id ? " · con usuario" : ""}</div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <button data-testid={`edit-member-${m.id}`} onClick={() => { setForm({ name: m.name, birth_date: m.birth_date || "", relation: m.relation }); setEditId(m.id); setOpen(true); }} className="text-muted-foreground hover:text-foreground"><Pencil size={15} /></button>
                  <button data-testid={`delete-member-${m.id}`} onClick={() => removeMember(m.id)} className="text-muted-foreground hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Editar miembro" : "Nuevo miembro"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="member-name-input" placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div>
              <label className="text-xs text-muted-foreground">Fecha de nacimiento (opcional)</label>
              <Input data-testid="member-birthdate-input" type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
            </div>
            <Select value={form.relation} onValueChange={(v) => setForm({ ...form, relation: v })}>
              <SelectTrigger data-testid="member-relation-select"><SelectValue /></SelectTrigger>
              <SelectContent>{RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="member-submit-button" onClick={submitMember} disabled={!form.name} className="w-full">{editId ? "Guardar" : "Añadir"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
